import { memoize } from 'lodash';
import { shallow } from 'zustand/shallow';
import type ChunkManager from './ChunkManager';
import { JUMP_SPEED_THRESHOLD } from './constants';
import {
  type EventState,
  type LatLngOpacity,
  PinToShadowTrackMode,
  type Point,
  type StarterType,
  type State,
} from './store';
import { matchesTagSelection } from './utils';
import { type LZ4Decoder, RenderInfoLayout } from './utils/decodeBinding';

const getColors = memoize((starters: Array<StarterType>): Uint8ClampedArray => {
  const colors = new Uint8ClampedArray(starters.length * 4);
  for (let i = 0; i < starters.length; i++) {
    const starter = starters[i];
    if (starter.markerColor == null) {
      colors[i * 4] = 255;
      colors[i * 4 + 1] = 255;
      colors[i * 4 + 2] = 255;
      colors[i * 4 + 3] = 255;
    } else {
      const tint = starter.markerColor.hexNum;
      colors[i * 4] = (0xff0000 & tint) >> 16;
      colors[i * 4 + 1] = (0x00ff00 & tint) >> 8;
      colors[i * 4 + 2] = 0x0000ff & tint;
      colors[i * 4 + 3] = 255;
    }
  }
  return colors;
});

// Per-dot stroke/outline color: a black or white ring picked to contrast the dot's own fill,
// so every dot stays visible regardless of the user-defined fill and the backplane base color.
// Reuses the precomputed `markerColor.isLight` (light fill → black stroke, dark fill → white).
const getStrokeColors = memoize((starters: Array<StarterType>): Uint8ClampedArray => {
  const strokeColors = new Uint8ClampedArray(starters.length * 4);
  for (let i = 0; i < starters.length; i++) {
    const starter = starters[i];
    const value = (starter.markerColor?.isLight ?? true) ? 0 : 255;
    strokeColors[i * 4] = value;
    strokeColors[i * 4 + 1] = value;
    strokeColors[i * 4 + 2] = value;
    strokeColors[i * 4 + 3] = 255;
  }
  return strokeColors;
});

export enum RenderItemVisibility {
  NONE = 0x0,
  PARTICLE = 0x1,
  FLAG = 0x2,
}

export class RenderInfoBuffer {
  static POSITION_SIZE = RenderInfoLayout.POSITION_ITEM_STRIDE;
  static COLOR_SIZE = 4;

  time: number;
  starters: Array<StarterType>;

  positions: Float32Array;
  offsets: Float64Array;
  timestamps: Float64Array;
  colors: Uint8ClampedArray;
  strokeColors: Uint8ClampedArray;
  visibility: Uint8ClampedArray;

  renderIndexOffsets: Map<number, number>;
  length: number;
  stale = false;

  private constructor(
    time: number,
    starters: Array<StarterType>,
    positions: Float32Array,
    offsets: Float64Array,
    timestamps: Float64Array,
    colors: Uint8ClampedArray,
    strokeColors: Uint8ClampedArray,
    visibility: Uint8ClampedArray,
    renderIndexOffsets: Map<number, number>,
    length: number,
  ) {
    this.time = time;
    this.starters = starters;
    this.positions = positions;
    this.offsets = offsets;
    this.timestamps = timestamps;
    this.colors = colors;
    this.strokeColors = strokeColors;
    this.visibility = visibility;
    this.renderIndexOffsets = renderIndexOffsets;
    this.length = length;
  }

  static blank(length: number): RenderInfoBuffer {
    return new RenderInfoBuffer(
      0,
      [],
      new Float32Array(RenderInfoBuffer.POSITION_SIZE * length),
      new Float64Array(length),
      new Float64Array(length),
      new Uint8ClampedArray(RenderInfoBuffer.COLOR_SIZE * length),
      new Uint8ClampedArray(RenderInfoBuffer.COLOR_SIZE * length),
      new Uint8ClampedArray(length),
      new Map(),
      length,
    );
  }

  static create(
    starters: Array<StarterType>,
    infoBuffer: RenderInfoLayout,
    eventIndex: number,
    selected: Set<string> = new Set(),
    selectedTags: Map<string, Array<string>> = new Map(),
    hidden: Set<string> = new Set(),
    showAllFlags = false,
  ): RenderInfoBuffer {
    const positions = new Float32Array(infoBuffer.positions);
    const offsets = new Float64Array(starters.length);
    const timestamps = new Float64Array(starters.length);
    const colors = getColors(starters);
    const strokeColors = getStrokeColors(starters);
    const visibility = new Uint8ClampedArray(starters.length);
    const renderIndexOffsets = new Map([[eventIndex, 0]]);

    for (let i = 0, len = starters.length; i < len; i += 1) {
      // Combine the per-frame animation fade with the user-selected marker-color alpha.
      colors[i * 4 + 3] = infoBuffer.getOpacity(i) * (starters[i].markerColor?.alpha ?? 1) * 255;
      offsets[i] = infoBuffer.getOffset(i);
      timestamps[i] = infoBuffer.getTimestamp(i);

      const starter = starters[i];
      const isSelected = selected.has(starter.id);
      if (
        !hidden.has(starter.id) &&
        (selectedTags.size === 0 || matchesTagSelection(starter.tags, selectedTags) || isSelected)
      ) {
        visibility[i] =
          !isSelected && !showAllFlags ? RenderItemVisibility.PARTICLE : RenderItemVisibility.FLAG;
      } else {
        visibility[i] = RenderItemVisibility.NONE;
      }
    }

    return new RenderInfoBuffer(
      0,
      starters,
      positions,
      offsets,
      timestamps,
      colors,
      strokeColors,
      visibility,
      renderIndexOffsets,
      starters.length,
    );
  }

  setBuffer(buffer: RenderInfoBuffer, offset: number): RenderInfoBuffer {
    this.time = buffer.time;
    this.positions.set(buffer.positions, offset * RenderInfoBuffer.POSITION_SIZE);
    this.offsets.set(buffer.offsets, offset);
    this.timestamps.set(buffer.timestamps, offset);
    this.colors.set(buffer.colors, offset * RenderInfoBuffer.COLOR_SIZE);
    this.strokeColors.set(buffer.strokeColors, offset * RenderInfoBuffer.COLOR_SIZE);
    this.visibility.set(buffer.visibility, offset);

    this.setStarters(buffer.starters, offset);

    this.stale = this.stale || buffer.stale;
    for (const [eventIndex, oldOffset] of buffer.renderIndexOffsets) {
      this.renderIndexOffsets.set(eventIndex, offset + oldOffset);
    }

    return buffer;
  }

  setStarters(starters: Array<StarterType>, offset: number): void {
    for (let i = 0; i < starters.length; i++) {
      this.starters[offset + i] = starters[i];
    }
  }

  getLat(i: number): number {
    return (
      this.positions[i * RenderInfoBuffer.POSITION_SIZE + 1] +
      this.positions[i * RenderInfoBuffer.POSITION_SIZE + 4]
    );
  }

  getLng(i: number): number {
    return (
      this.positions[i * RenderInfoBuffer.POSITION_SIZE + 0] +
      this.positions[i * RenderInfoBuffer.POSITION_SIZE + 3]
    );
  }

  getLatLngOpacity(i: number): LatLngOpacity {
    const lng = this.getLng(i);
    const lat = this.getLat(i);
    let opacity = this.getOpacity(i);

    opacity =
      opacity > 0 &&
      Number.isFinite(lat) &&
      lat >= -90 &&
      lat <= 90 &&
      lat !== 0 &&
      Number.isFinite(lng) &&
      lng >= -180 &&
      lng <= 180 &&
      lng !== 0
        ? opacity
        : 0;
    return { lng, lat, opacity };
  }

  getPoint(i: number): Point {
    return {
      lng: this.getLng(i),
      lat: this.getLat(i),
      time: this.time,
      offset: this.getOffset(i),
    };
  }

  getTimestamp(i: number): number {
    return this.timestamps[i];
  }

  getTime(i: number): Date {
    return new Date(this.timestamps[i]);
  }

  getOffset(i: number): number {
    return this.offsets[i];
  }

  getOpacity(i: number): number {
    const opacity = this.colors[i * RenderInfoBuffer.COLOR_SIZE + 3];
    return opacity == null ? 0 : opacity;
  }

  getColor(i: number): string {
    const o = i * RenderInfoBuffer.COLOR_SIZE;
    return `rgb(${this.colors[o]}, ${this.colors[o + 1]}, ${this.colors[o + 2]})`;
  }

  getStrokeColor(i: number): string {
    const o = i * RenderInfoBuffer.COLOR_SIZE;
    return `rgb(${this.strokeColors[o]}, ${this.strokeColors[o + 1]}, ${this.strokeColors[o + 2]})`;
  }

  getShowParticle(i: number): boolean {
    return this.visibility[i] === RenderItemVisibility.PARTICLE;
  }

  getShowMarker(i: number): boolean {
    return this.visibility[i] === RenderItemVisibility.FLAG;
  }

  getIsVisible(i: number): boolean {
    return this.visibility[i] !== RenderItemVisibility.NONE;
  }

  getRenderIndex(eventIndex: number, starterIndex: number): number {
    return (this.renderIndexOffsets.get(eventIndex) ?? 0) + starterIndex;
  }

  debug(): Array<LatLngOpacity> {
    const output = [];
    for (let i = 0; i < this.length; i++) {
      output.push(this.getLatLngOpacity(i));
    }
    return output;
  }
}

const getDecoder = (() => {
  // Memoize strategy: Keep one decoder per event, invalidated when ChunkManager changes
  const cache = new Map<string, { decoder: LZ4Decoder; chunkManager: ChunkManager }>();
  const promiseCache = new Map<
    string,
    { promise: Promise<LZ4Decoder>; chunkManager: ChunkManager }
  >();

  return (eventId: string, chunkManager: ChunkManager): LZ4Decoder | null => {
    const cached = cache.get(eventId);
    const pending = promiseCache.get(eventId);

    // Invalidate if the ChunkManager instance changed (e.g. shadow track update)
    if (cached != null && cached.chunkManager === chunkManager) {
      return cached.decoder;
    }
    if (pending != null && pending.chunkManager === chunkManager) {
      return null;
    }

    // ChunkManager changed or first load — create a new decoder
    cache.delete(eventId);
    promiseCache.set(eventId, {
      promise: chunkManager.createDecoder().then((decoder) => {
        cache.set(eventId, { decoder, chunkManager });
        promiseCache.delete(eventId);
        return decoder;
      }),
      chunkManager,
    });
    return null;
  };
})();

const getEventRenderInfoBuffer = (() => {
  // Memoize strategy: Keep one RenderInfoBuffer per event
  type Info = {
    eventId: string;
    time: number;
    selected: Array<string>;
    selectedTags: Map<string, Array<string>>;
    hidden: Set<string>;
    pinToShadowTrack: PinToShadowTrackMode;
    chunkManager: ChunkManager;
    starters: Array<StarterType>;
    markerTimeout: number;
    chunk: ArrayBufferLike;
    decoder: LZ4Decoder;
  };

  const memo = (() => {
    const cache: Array<{ info: Info; value: RenderInfoBuffer }> = [];
    return {
      get: (info: Info) => cache.find((item) => shallow(item.info, info))?.value,
      set: (info: Info, value: RenderInfoBuffer) => {
        const existingIndex = cache.findIndex((item) => item.info.eventId === info.eventId);
        if (existingIndex >= 0) {
          cache[existingIndex].info = info;
          cache[existingIndex].value = value;
        } else {
          cache.push({ info, value });
        }
      },
    };
  })();

  return function getEventRenderInfoBuffer(
    state: State,
    time: number,
    event: EventState,
    chunkManager: ChunkManager,
  ): RenderInfoBuffer | null {
    const {
      timer: { selected, selectedTags, hidden },
      options: { pinToShadowTrack },
    } = state;

    const {
      eventId,
      index: eventIndex,
      starters,
      chunkedShadowTrack,
      options: { markerTimeout },
    } = event;

    const chunk = chunkManager.getChunkSync(time);
    const decoder = getDecoder(event.eventId, chunkManager);

    if (chunk == null || decoder == null) {
      return null;
    }

    const memoizeInfo: Info = {
      eventId,
      time,
      selected,
      selectedTags,
      hidden,
      pinToShadowTrack,
      chunkManager,
      starters,
      markerTimeout,
      chunk,
      decoder,
    };

    const memoizeValue = memo.get(memoizeInfo);
    if (memoizeValue != null) {
      return memoizeValue;
    }

    const usesOffsetInterpolation =
      (pinToShadowTrack === PinToShadowTrackMode.FORCED ||
        pinToShadowTrack === PinToShadowTrackMode.MAGNETIC) &&
      chunkedShadowTrack != null;

    decoder.updateDecodedChunk(chunk);
    const interpolatedData = usesOffsetInterpolation
      ? decoder.interpolateOffsets(
          time,
          markerTimeout,
          pinToShadowTrack === PinToShadowTrackMode.MAGNETIC,
          JUMP_SPEED_THRESHOLD,
        )
      : decoder.interpolate(time, markerTimeout);

    const showAllFlags = state.options.showAllFlags;

    const buffer = RenderInfoBuffer.create(
      starters,
      interpolatedData,
      eventIndex,
      new Set(selected),
      selectedTags,
      hidden,
      showAllFlags,
    );

    memo.set(memoizeInfo, buffer);
    return buffer;
  };
})();

export const getRenderInfoBuffer = (() => {
  const bufferCache = new Map();

  function getCachedEventRenderInfoBuffer(
    state: State,
    event: EventState,
    chunkManager: ChunkManager,
  ): RenderInfoBuffer | null {
    const eventBuffer = getEventRenderInfoBuffer(state, state.timer.time, event, chunkManager);
    if (eventBuffer != null) {
      bufferCache.set(event.eventId, eventBuffer);
      return eventBuffer;
    }
    // Show stale buffer again
    const cachedEventBuffer = bufferCache.get(event.eventId);
    if (cachedEventBuffer != null) {
      cachedEventBuffer.stale = true;
      return cachedEventBuffer;
    }
    return null;
  }

  return function getRenderInfoBuffer(state: State) {
    const currentEventIndices = state.currentEventIndices;
    if (currentEventIndices.length === 1) {
      const event = state.events[currentEventIndices[0]];
      const eventBuffer = getCachedEventRenderInfoBuffer(
        state,
        event,
        state.eventsChangeable[currentEventIndices[0]].chunkManager,
      );
      if (eventBuffer != null) {
        return eventBuffer;
      }
      return RenderInfoBuffer.blank(event.starters.length);
    }
    const startersCount = currentEventIndices
      .map((i) => state.events[i].starters.length)
      .reduce((r, a) => r + a, 0);
    const buffer = RenderInfoBuffer.blank(startersCount);

    let j = 0;
    for (const i of currentEventIndices) {
      const event = state.events[i];
      const chunkManager = state.eventsChangeable[i].chunkManager;
      const eventBuffer = getCachedEventRenderInfoBuffer(state, event, chunkManager);
      if (eventBuffer != null) {
        buffer.setBuffer(eventBuffer, j);
      } else {
        buffer.setStarters(event.starters, j);
        buffer.renderIndexOffsets.set(i, j);
      }
      j += event.starters.length;
    }
    return buffer;
  };
})();
